import { differenceInCalendarDays } from 'date-fns';
import { paymentMethod as PaymentMethod, paymentType as PaymentType } from '@ourbranch/lookups';

import { awsDateToDateObject } from 'core/helpers/formatters';

export const PriceDetailsVariant = Object.freeze({
  Escrow: 'escrow',
  OneTime: 'oneTime',
  EvenPayments: 'evenPayments',
  DueAtBind: 'dueAtBind'
});

// Separate 1st and 2nd payments this many days apart or fewer get the close-payments callout, the same
// window in which `canOfferMegaDownPay` lets the agent combine them.
export const CLOSE_PAYMENTS_DAYS = 15;

// The billed amount of a schedule row, the convention every schedule view uses.
export const amountOf = (payment) => payment?.total ?? payment?.remainingMonthly;
const toCents = (amount) => Math.round(Number(amount) * 100);

/**
 * Which price details a policy card shows (#36357), read from the policy's saved payment method and type
 * and the server-computed schedule, never from an assumed billing day.
 *
 * Monthly policies pick their layout by comparing the actual 1st and 2nd payment amounts. Whether the 1st
 * payment combines the 1st and 2nd is read from the schedule too: billing drops one installment when it
 * combines them (11 for a 12-month term, 5 for a 6-month one), whether the agent chose to or billing
 * day = effective day forces it. So the card reads correctly both before and after #35596 stops forcing it.
 *
 * Returns null when a monthly policy has no schedule to show (an unpriced line), and for the installment
 * plans only carriers sell (auto 2-pay, farm quarterly and half-term), which have no variant here.
 */
export const getPriceDetails = ({ paymentMethod, paymentType, total, schedule, termMonths }) => {
  if (paymentMethod === PaymentMethod.Escrow) {
    return { variant: PriceDetailsVariant.Escrow, amount: total };
  }
  if (paymentType === PaymentType.OneTime) {
    return { variant: PriceDetailsVariant.OneTime, amount: total };
  }
  if (paymentType !== PaymentType.Monthly || !schedule?.length) {
    return null;
  }

  const [firstPayment, secondPayment] = schedule;
  if (!secondPayment) {
    return { variant: PriceDetailsVariant.OneTime, amount: amountOf(firstPayment) };
  }

  const isCombined = schedule.length === termMonths - 1;
  const daysApart = differenceInCalendarDays(
    awsDateToDateObject(secondPayment.date),
    awsDateToDateObject(firstPayment.date)
  );
  const daysToSecondPayment = !isCombined && daysApart <= CLOSE_PAYMENTS_DAYS ? daysApart : null;

  if (toCents(amountOf(firstPayment)) === toCents(amountOf(secondPayment))) {
    return {
      variant: PriceDetailsVariant.EvenPayments,
      paymentCount: schedule.length,
      paymentAmount: amountOf(firstPayment),
      termTotal: total,
      daysToSecondPayment
    };
  }

  return {
    variant: PriceDetailsVariant.DueAtBind,
    dueAtBindAmount: amountOf(firstPayment),
    paymentCount: schedule.length - 1,
    paymentAmount: amountOf(secondPayment),
    termTotal: total,
    isCombined,
    daysToSecondPayment
  };
};
